import { createContext, useContext, useState, useEffect, useRef } from "react";
import { adminAuthApiClient } from "../services/adminAuthApiClient";

const AdminAuthContext = createContext({
  admin: null,
  isAuthenticated: false,
  authLoading: false,
  loginSuccess: async () => {},
  logout: async () => {}
});

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  
  const isMounted = useRef(true);
  const initialCheckDone = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ✅ Lazy auth check - only check when explicitly called
  const checkAuth = async () => {
    if (!isMounted.current || initialCheckDone.current) return;

    try {
      setAuthLoading(true);
      const res = await adminAuthApiClient.get("api/auth/admin/me");
      
      if (isMounted.current) {
        setAdmin(res.data);
        initialCheckDone.current = true;
      }
      
      return res.data;
    } catch (err) {
      if (isMounted.current) {
        setAdmin(null);
        initialCheckDone.current = true;
      }
      throw err;
    } finally {
      if (isMounted.current) {
        setAuthLoading(false);
      }
    }
  };

  // ✅ Only check auth when needed (not on every mount)
  const loginSuccess = async () => {
    try {
      setAuthLoading(true);
      const res = await adminAuthApiClient.get("api/auth/admin/me");
      
      if (isMounted.current) {
        setAdmin(res.data);
        initialCheckDone.current = true;
      }
      
      return res.data;
    } catch (err) {
      console.error("Auth check after login failed:", err.response?.status);
      if (isMounted.current) {
        setAdmin(null);
      }
      throw err;
    } finally {
      if (isMounted.current) {
        setAuthLoading(false);
      }
    }
  };

  const clearAllCookies = () => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    
    const cookies = [
      { name: 'access_token', path: '/' },
      { name: 'refresh_token', path: '/api/auth/admin/refresh' },
      { name: 'refresh_token', path: '/' },
      { name: 'refreshToken', path: '/' },
    ];

    cookies.forEach(cookie => {
      document.cookie = `${cookie.name}=; path=${cookie.path}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      
      if (!isLocalhost) {
        document.cookie = `${cookie.name}=; domain=.${hostname}; path=${cookie.path}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      }
    });
  };

  const logout = async () => {
    try {
      await adminAuthApiClient.post("/api/auth/admin/logout");
    } catch (err) {
      console.error("Logout error", err);
    } finally {
      if (isMounted.current) {
        setAdmin(null);
        initialCheckDone.current = false;
      }
      clearAllCookies();
      
      window.location.href = '/admin-login?logout=success';
    }
  };

  // Debug: Log state changes
  // useEffect(() => {
  //   console.log("📊 Auth State:", 
  //     {
  //     admin: !!admin,
  //     authLoading,
  //     timestamp: new Date().toISOString()
  //   })
  //   ;
  // }, [admin, authLoading]);

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        authLoading,
        isAuthenticated: !!admin,
        loginSuccess,
        checkAuth,
        adminLogout: logout
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);

  
